// FILE: src/pages/AdminResourceListPage.jsx
// Lessons (WP-5; R1, matrix 9.7): every chapter and homelink with its
// curriculum, kind, slides and duration. Filters: a curriculum (sent to the
// server, ?curriculum=<id>|none) and the lesson type (tabs, ?type=). Both are
// kept in the URL so they survive reload and Back. Each title opens the
// lesson's own page; "Add a lesson" opens the create form.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import fetchWithAuth from "../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { FilterTabs, ResponsiveTable } from "../components/admin/AdminBits";
import { CurriculumStatusChip, PracticeChip } from "../components/admin/ContentBits";
import { curriculumName } from "../components/admin/content";
import { formatDuration } from "../utils/timeFormat";
import { curriculumStatusLabel, resourceKindLabel, resourceTypeLabel } from "../i18n/labels";
import { useCan } from "../hooks/usePermissions";

const VIEWS = [
  { value: "all", label: "All lessons" },
  { value: "chapter", label: "Chapters", type: "CHAPTER" },
  { value: "homelink", label: "Homelinks", type: "HOMELINK" }
];
const TYPE_ORDER = { CHAPTER: 0, HOMELINK: 1 };

function slideCount(r) {
  return r.totalSlides || (Array.isArray(r.slides) ? r.slides.length : 0);
}

/** "Chapter", "Homelink" or "Chapter: graded assessment". */
export function kindText(r) {
  const type = resourceTypeLabel(r.resourceType);
  if (r.resourceType === "CHAPTER" && r.kind === "ASSESSMENT") return `${type}: ${resourceKindLabel("ASSESSMENT").toLowerCase()}`;
  return type;
}

export default function AdminResourceListPage() {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some((v) => v.value === params.get("type")) ? params.get("type") : "all";
  const curriculum = params.get("curriculum") || "";

  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [curricula, setCurricula] = useState([]);
  const [state, setState] = useState({ list: null, loading: true, error: null });
  const latest = useRef(0);
  const canEdit = useCan("content.edit");

  useEffect(() => {
    fetchWithAuth("/api/admin/curricula")
      .then((list) => setCurricula(Array.isArray(list) ? list : []))
      .catch(() => setCurricula([]));
  }, []);

  const load = useCallback(() => {
    const id = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    const q = curriculum ? `?curriculumId=${encodeURIComponent(curriculum)}` : "";
    fetchWithAuth(`/api/admin/resources${q}`)
      .then((list) => {
        if (id === latest.current) setState({ list: Array.isArray(list) ? list : [], loading: false, error: null });
      })
      .catch((error) => {
        if (id === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [curriculum]);

  useEffect(() => {
    load();
  }, [load]);

  const byId = useMemo(() => Object.fromEntries(curricula.map((c) => [c.id, c])), [curricula]);
  const rank = useMemo(() => {
    const ids = curricula.map((c) => c.id);
    return (r) => (r.curriculumId ? ids.indexOf(String(r.curriculumId)) : ids.length);
  }, [curricula]);

  const resources = useMemo(
    () =>
      (state.list || [])
        .slice()
        .sort(
          (a, b) =>
            rank(a) - rank(b) ||
            (TYPE_ORDER[a.resourceType] ?? 2) - (TYPE_ORDER[b.resourceType] ?? 2) ||
            (Number(a.order) || 0) - (Number(b.order) || 0)
        ),
    [state.list, rank]
  );
  const counts = {
    all: resources.length,
    chapter: resources.filter((r) => r.resourceType === "CHAPTER").length,
    homelink: resources.filter((r) => r.resourceType === "HOMELINK").length
  };
  const type = VIEWS.find((v) => v.value === view)?.type;
  const shown = type ? resources.filter((r) => r.resourceType === type) : resources;

  const setParam = (key, value, empty) => {
    const next = new URLSearchParams(params);
    if (!value || value === empty) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const chosen = curriculum && curriculum !== "none" ? byId[curriculum] : null;
  const newLink = `/admin/resources/new${chosen && chosen.status === "DRAFT" ? `?curriculumId=${chosen.id}` : ""}`;
  const { list, loading, error } = state;

  return (
    <Box>
      <PageHeader
        title="Lessons"
        description="The chapters and homelinks students take, by curriculum and in course order. Open a lesson to see who uses it, change it or check its slides."
        actions={
          <>
            <Button component={RouterLink} to="/admin/curricula" variant="outlined">
              Curricula
            </Button>
            {canEdit && (
              <Button component={RouterLink} to={newLink} variant="contained" startIcon={<AddIcon />}>
                Add a lesson
              </Button>
            )}
          </>
        }
      />

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Section id="lessons" title="Course lessons" contentSx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2, alignItems: { sm: "center" } }}>
          <TextField
            select
            id="curriculum-filter"
            label="Curriculum"
            value={curriculum}
            onChange={(e) => setParam("curriculum", e.target.value, "")}
            sx={{ minWidth: { sm: 320 }, maxWidth: "100%" }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            <MenuItem value="">All curricula</MenuItem>
            {curricula.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {`${curriculumName(c)} (${curriculumStatusLabel(c.status).toLowerCase()})`}
              </MenuItem>
            ))}
            <MenuItem value="none">Not in a curriculum</MenuItem>
          </TextField>
          {chosen && <CurriculumStatusChip status={chosen.status} />}
        </Stack>

        {list === null && error ? (
          <ErrorState error={error} title="We couldn't load the lessons" onRetry={load} />
        ) : list === null ? (
          <LoadingState variant="table" rows={6} label="Loading lessons…" />
        ) : (
          <>
            {error && <ErrorState compact error={error} onRetry={load} />}
            <FilterTabs
              label="Lesson type"
              idPrefix="lessons"
              value={view}
              onChange={(v) => setParam("type", v, "all")}
              options={VIEWS.map((v) => ({ ...v, count: counts[v.value] }))}
            />
            <Box id="lessons-panel" role="tabpanel" aria-labelledby={`lessons-tab-${view}`}>
              <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the lessons" />}</Box>
              {shown.length === 0 ? (
                <EmptyState
                  compact
                  title={resources.length ? "No lessons of this type" : "No lessons yet"}
                  description={
                    resources.length
                      ? "Choose another tab to see the other lessons."
                      : "Add a lesson to a draft curriculum, or choose another curriculum above."
                  }
                />
              ) : (
                <ResponsiveTable label="Lessons" minWidth={820}>
                  <TableHead>
                    <TableRow>
                      <TableCell component="th" scope="col" align="right">
                        Position
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Title
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Type
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Curriculum
                      </TableCell>
                      <TableCell component="th" scope="col" align="right">
                        Slides
                      </TableCell>
                      <TableCell component="th" scope="col" align="right">
                        Total duration
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.map((r) => {
                      const title = r.title || "Untitled lesson";
                      const c = r.curriculumId ? byId[String(r.curriculumId)] : null;
                      return (
                        <TableRow key={r._id} hover>
                          <TableCell align="right">{r.order ?? "—"}</TableCell>
                          <TableCell component="th" scope="row">
                            <Link component={RouterLink} to={`/admin/resources/${r._id}`} sx={{ fontWeight: 600 }}>
                              {title}
                            </Link>
                            {r.description && (
                              <Typography
                                sx={{
                                  color: "text.secondary",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  maxWidth: "60ch"
                                }}
                              >
                                {r.description}
                              </Typography>
                            )}
                            {r.isPractice && <PracticeChip sx={{ mt: 0.75 }} />}
                          </TableCell>
                          <TableCell>{kindText(r)}</TableCell>
                          <TableCell>{r.curriculumId ? (c ? curriculumName(c) : "—") : "Not in a curriculum"}</TableCell>
                          <TableCell align="right">{slideCount(r)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {r.totalDuration ? formatDuration(r.totalDuration) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ResponsiveTable>
              )}
            </Box>
          </>
        )}
      </Section>
    </Box>
  );
}
